import React, { useEffect, useState } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import { toast } from "react-toastify";
import { Box, Typography } from "@mui/material";
import { Phone, Clock, PhoneCall, Hash, Signal } from "lucide-react";
import {
  fetchConnectedNumber,
  fetchLastConnectedTime,
  fetchLastSpokenNumber,
  fetchMode,
  fetchQr,
  fetchStatus,
  postLogout,
} from "../../services/whatsAppStatusApi";
import { styled } from "@mui/material/styles";

interface WhatsAppMenuModalProps {
  onClose: () => void;
  isOpen: boolean;
}

interface WhatsAppData {
  connectedNumber?: string;
  lastConnectedTime?: string;
  lastSpokenNumber?: string;
  phoneNumber?: string;
  status?: string;
}

const InfoRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "12px 16px",
  borderBottom: "1px solid #404040",
  gap: "12px",
  "&:last-child": {
    borderBottom: "none",
  },
});

const IconWrapper = styled(Box)({
  color: "#22C55E",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
});

const InfoText = styled(Typography)({
  color: "#22C55E",
  fontWeight: 500,
  flex: 1,
});

const InfoValue = styled(Typography)({
  color: "#FFFFFF",
  textAlign: "right",
});

const ActionButton = styled("button")(({ color = "#22C55E" }) => ({
  width: "100%",
  padding: "12px",
  backgroundColor: color,
  color: "#FFFFFF",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: 500,
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: color === "#22C55E" ? "#16A34A" : "#EF4444",
  },
  "&:disabled": {
    backgroundColor: "#4B5563",
    cursor: "not-allowed",
  },
}));

function WhatsAppMenuModal({ onClose, isOpen }: WhatsAppMenuModalProps) {
  const [data, setData] = useState<WhatsAppData>({});
  const [qrImageUrl, setQrImageUrl] = useState<string>("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [textConfig, setTextConfig] = useState<Record<string, string>>({});
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchData = async () => {
    const newData: WhatsAppData = {};

    try {
      const connectedNumber = await fetchConnectedNumber();
      newData.connectedNumber =
        connectedNumber.connectedNumber || "No hay datos";
    } catch (error) {
      newData.connectedNumber = "No hay datos";
    }

    try {
      const lastConnectedTime = await fetchLastConnectedTime();
      newData.lastConnectedTime =
        lastConnectedTime.lastConnectedTime || "No hay datos";
    } catch (error) {
      newData.lastConnectedTime = "No hay datos";
    }

    try {
      const lastSpokenNumber = await fetchLastSpokenNumber();
      newData.lastSpokenNumber =
        lastSpokenNumber.lastSpokenNumber || "No hay datos";
    } catch (error) {
      newData.lastSpokenNumber = "No hay datos";
    }

    try {
      const mode = await fetchMode();
      newData.phoneNumber = mode.phoneNumber || "No hay datos";
    } catch (error) {
      newData.phoneNumber = "No hay datos";
    }

    try {
      const status = await fetchStatus();
      newData.status = status.connectionStatus || "No hay datos";
    } catch (error) {
      newData.status = "No hay datos";
    }

    if (
      !newData.connectedNumber ||
      newData.connectedNumber === "No hay datos"
    ) {
      try {
        const qrBlob = await fetchQr();
        const qrImageUrl = URL.createObjectURL(qrBlob);
        setQrImageUrl(qrImageUrl);
      } catch (error) {
        setQrImageUrl("");
      }
    }

    setData(newData);
    if (initialLoading) setInitialLoading(false);
  };

  const handleLogout = async () => {
    setIsButtonDisabled(true);
    toast.warning(textConfig.WHATSAPP_MENU_TEXT_MAKING_REQUEST);
    try {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      await postLogout();
      await fetchData();
      toast.success(textConfig.WHATSAPP_MENU_TOAST_TEXT_LOGOUT);
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
      toast.error(textConfig.WHATSAPP_MENU_TOAST_TEXT_ERROR_LOGOUT);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  useEffect(() => {
    const fetchTextConfig = async () => {
      try {
        const response = await fetch("/text.json");
        const data = await response.json();
        setTextConfig(data);
      } catch (error) {
        console.error("Error loading text config:", error);
      }
    };

    fetchTextConfig();
    fetchData();
    const interval = setInterval(fetchData, 10000);

    return () => clearInterval(interval);
  }, []);

  return (
    <BaseModal
      title={textConfig.WHATSAPP_MENU_TEXT_TITLE || "Servicio de WhatsApp"}
      onClose={onClose}
      isOpen={isOpen}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <InfoRow>
          <IconWrapper>
            <Phone size={20} />
          </IconWrapper>
          <InfoText>Número Conectado</InfoText>
          <InfoValue>{data.connectedNumber}</InfoValue>
        </InfoRow>

        <InfoRow>
          <IconWrapper>
            <Clock size={20} />
          </IconWrapper>
          <InfoText>Última Conexión</InfoText>
          <InfoValue>{data.lastConnectedTime}</InfoValue>
        </InfoRow>

        <InfoRow>
          <IconWrapper>
            <PhoneCall size={20} />
          </IconWrapper>
          <InfoText>Último Número Hablado</InfoText>
          <InfoValue>{data.lastSpokenNumber}</InfoValue>
        </InfoRow>

        <InfoRow>
          <IconWrapper>
            <Hash size={20} />
          </IconWrapper>
          <InfoText>Número del Teléfono</InfoText>
          <InfoValue>{data.phoneNumber}</InfoValue>
        </InfoRow>

        <InfoRow>
          <IconWrapper>
            <Signal size={20} />
          </IconWrapper>
          <InfoText>Estado de Conexión</InfoText>
          <InfoValue>{data.status}</InfoValue>
        </InfoRow>

        {(!data.connectedNumber || data.connectedNumber === "No hay datos") &&
          qrImageUrl && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 2,
                mb: 2,
              }}
            >
              <img
                src={qrImageUrl}
                alt="Código QR"
                style={{
                  maxWidth: "200px",
                  maxHeight: "200px",
                }}
              />
              <Typography
                sx={{
                  color: "#8B8B84",
                  mt: 1,
                  fontSize: "14px",
                }}
              >
                Código QR
              </Typography>
            </Box>
          )}

        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <ActionButton onClick={handleLogout} disabled={isButtonDisabled}>
            Cerrar Sesión
          </ActionButton>
          <ActionButton onClick={onClose} color="#DC2626">
            Cerrar
          </ActionButton>
        </Box>
      </Box>
    </BaseModal>
  );
}

export default WhatsAppMenuModal;
